import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UsersService, type CreateUserDto, type Role } from '../../services/users/users.service';
import { NotificationToast } from '../ui/NotificationToast';
import { User, Shield, Eye, Settings, CheckCircle, XCircle, AlertCircle, UserCog, Users, Check, LayoutGrid } from 'lucide-react';

const createUserSchema = z.object({
  email: z.string().min(1, 'Email es requerido').email('Email inválido'),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres'),
  confirmPassword: z.string().min(1, 'Debe confirmar la contraseña'),
  nombreCompleto: z.string().min(1, 'Nombre completo es requerido').min(2, 'Mínimo 2 caracteres'),
  tipoUsuario: z.enum(['administrador', 'cliente', 'vendedor']),
  usuario: z.string().optional(),
  rol: z.string().min(1, 'Debe seleccionar un rol')
}).refine((data) => data.password === data.confirmPassword, {
  message: "Las contraseñas no coinciden",
  path: ["confirmPassword"],
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

interface CreateUserDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateUserDrawer: React.FC<CreateUserDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [createdUser, setCreatedUser] = useState<any>(null);
  
  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);
  
  // Estados para roles
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  
  // Estados para dropdowns
  const [tipoUsuarioDropdownOpen, setTipoUsuarioDropdownOpen] = useState(false);
  const [rolDropdownOpen, setRolDropdownOpen] = useState(false);
  
  // Estados para validación de contraseñas
  const [passwordStrength, setPasswordStrength] = useState<{
    score: number;
    label: string;
    color: string;
    suggestions: string[];
  }>({ score: 0, label: 'Débil', color: 'text-red-500', suggestions: [] });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    control
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      tipoUsuario: 'cliente'
      // roles: [] // Temporalmente comentado
    }
  });

  const tipoUsuario = watch('tipoUsuario');
  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  // Función para evaluar la fortaleza de la contraseña
  const evaluatePasswordStrength = (pwd: string) => {
    let score = 0;
    const suggestions = [];

    if (pwd.length >= 8) score++;
    else suggestions.push('Mínimo 8 caracteres');

    if (/[a-z]/.test(pwd)) score++;
    else suggestions.push('Al menos una letra minúscula');

    if (/[A-Z]/.test(pwd)) score++;
    else suggestions.push('Al menos una letra mayúscula');

    if (/\d/.test(pwd)) score++;
    else suggestions.push('Al menos un número');

    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) score++;
    else suggestions.push('Al menos un carácter especial');

    let label, color;
    if (score <= 2) {
      label = 'Débil';
      color = 'text-red-500';
    } else if (score === 3) {
      label = 'Media';
      color = 'text-yellow-500';
    } else if (score === 4) {
      label = 'Fuerte';
      color = 'text-green-500';
    } else {
      label = 'Muy fuerte';
      color = 'text-green-600';
    }

    return { score, label, color, suggestions };
  };

  // Evaluar la fortaleza cuando cambia la contraseña
  useEffect(() => {
    if (password) {
      setPasswordStrength(evaluatePasswordStrength(password));
    } else {
      setPasswordStrength({ score: 0, label: 'Débil', color: 'text-red-500', suggestions: [] });
    }
  }, [password]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      // Cargar roles cuando se abre el drawer
      loadRoles();
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
      // Reset state when closing
      setTimeout(() => {
        setIsLoading(false);
        setErrorMessage(null);
        setCreatedUser(null);
        reset();
      }, 300);
    }
  }, [isOpen, reset]);

  const loadRoles = async () => {
    setIsLoadingRoles(true);
    try {
      const rolesData = await UsersService.getRoles();
      setRoles(rolesData.filter(role => role.activo)); // Solo roles activos
    } catch (error) {
      console.error('Error al cargar roles:', error);
      setErrorMessage('Error al cargar los roles disponibles');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const handleClose = () => {
    setIsVisible(false);
    // Llamar onClose después de un pequeño delay para permitir la animación
    setTimeout(() => {
      onClose();
    }, 300); // Mismo tiempo que la duración de la transición CSS
  };

  const onSubmit = async (data: CreateUserFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      const userData: CreateUserDto = {
        email: data.email,
        password: data.password,
        nombreCompleto: data.nombreCompleto,
        tipoUsuario: data.tipoUsuario,
        roles: [data.rol] // Solo un rol asignado
      };

      const result = await UsersService.createUser(userData);
      
      setCreatedUser({
        id: result.id,
        email: result.email,
        nombreCompleto: result.nombreCompleto,
        tipoUsuario: result.tipoUsuario
      });

      // Mostrar toast de éxito
      setNotificationConfig({
        title: '¡Usuario creado!',
        message: `El usuario ${result.nombreCompleto} fue creado exitosamente.`,
        type: 'success'
      });
      setShowNotification(true);

      // Cerrar el drawer después de un momento
      setTimeout(() => {
        handleClose();
      }, 2000);
      
      // Si fue exitoso y hay callback, ejecutarlo
      if (onSuccess) {
        onSuccess();
      }
      
    } catch (error) {
      console.error('Error al crear usuario:', error);
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido al crear usuario';
      setErrorMessage(errorMsg);
      
      // Mostrar toast de error
      setNotificationConfig({
        title: 'Error al crear usuario',
        message: errorMsg,
        type: 'error'
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    handleClose();
  };

  const tiposUsuario = [
    { 
      value: 'cliente', 
      label: 'Cliente',
      description: 'Acceso básico al sistema',
      icon: User,
      color: 'bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100'
    },
    { 
      value: 'vendedor', 
      label: 'Vendedor',
      description: 'Acceso para gestionar ventas',
      icon: UserCog,
      color: 'bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100'
    },
    { 
      value: 'administrador', 
      label: 'Administrador',
      description: 'Acceso completo al sistema',
      icon: Shield,
      color: 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100'
    }
  ];

  // Custom Dropdown Component
  const CustomDropdown: React.FC<{
    value: string;
    onValueChange: (value: string) => void;
    options: Array<{
      value: string;
      label: string;
      description?: string;
      icon?: React.ComponentType<any> | string;
      color?: string;
    }>;
    placeholder: string;
    isOpen: boolean;
    onToggle: () => void;
    error?: boolean;
  }> = ({ value, onValueChange, options, placeholder, isOpen, onToggle, error = false }) => {
    const selectedOption = options.find(option => option.value === value);
    const dropdownRef = React.useRef<HTMLDivElement>(null);

    // Cerrar dropdown al hacer clic fuera
    React.useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          if (isOpen) onToggle();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, onToggle]);

    return (
      <div ref={dropdownRef} className="relative">
        <button
          type="button"
          onClick={onToggle}
          className={`group w-full px-4 py-3 border rounded-lg transition-all duration-200 text-left flex items-center justify-between shadow-sm ${
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
              : isOpen 
                ? 'border-purple-500 focus:border-purple-500 focus:ring-purple-500/20 ring-4 ring-purple-500/20 bg-purple-50/30'
                : 'border-gray-300 focus:border-purple-500 hover:border-purple-400 hover:bg-purple-50/20'
          } focus:outline-none`}
        >
          <div className="flex items-center min-w-0 flex-1">
            {selectedOption ? (
              <>
                {selectedOption.icon && (
                  <div className="mr-3 shrink-0">
                    {typeof selectedOption.icon === 'string' ? (
                      <span className="text-lg">{selectedOption.icon}</span>
                    ) : (
                      <selectedOption.icon className="w-5 h-5" />
                    )}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">{selectedOption.label}</div>
                  {selectedOption.description && (
                    <div className="text-xs text-gray-500 truncate mt-0.5">{selectedOption.description}</div>
                  )}
                </div>
              </>
            ) : (
              <span className="text-gray-500 font-medium">{placeholder}</span>
            )}
          </div>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-all duration-200 shrink-0 ml-2 ${
              isOpen ? 'rotate-180 text-purple-500' : 'group-hover:text-purple-400'
            }`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-auto animate-in slide-in-from-top-2 duration-200">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onValueChange(option.value);
                  onToggle();
                }}
                className={`w-full px-4 py-3 text-left transition-all duration-150 border-b border-gray-50 last:border-b-0 first:rounded-t-xl last:rounded-b-xl ${
                  value === option.value 
                    ? 'bg-purple-100 border-purple-200 shadow-sm' 
                    : option.color || 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center">
                  {option.icon && (
                    <div className="mr-3 shrink-0">
                      {typeof option.icon === 'string' ? (
                        <span className="text-xl">{option.icon}</span>
                      ) : (
                        <option.icon className="w-5 h-5" />
                      )}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className={`font-medium truncate ${
                      value === option.value ? 'text-purple-900' : ''
                    }`}>
                      {option.label}
                    </div>
                    {option.description && (
                      <div className={`text-xs mt-1 truncate ${
                        value === option.value ? 'text-purple-700' : 'opacity-75'
                      }`}>
                        {option.description}
                      </div>
                    )}
                  </div>
                  {value === option.value && (
                    <CheckCircle className="w-5 h-5 text-purple-600 shrink-0 ml-2" />
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-20 overflow-hidden" style={{ top: '90px' }}>
      <div className="absolute inset-0 overflow-hidden">
        {/* Overlay */}
        <div 
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />
        
        {/* Drawer */}
        <section className={`absolute right-0 top-0 h-full w-full max-w-6xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div 
            className="h-full flex flex-col bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-8 py-5 bg-gradient-to-r from-purple-600 to-indigo-600 border-b border-purple-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <User className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Crear Nuevo Usuario
                    </h2>
                    <p className="text-sm text-purple-100 mt-0.5">
                      Registra un nuevo usuario en el sistema
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="text-white/70 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-8">
              {/* Errores de validación */}
              {errorMessage && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-800 text-sm">{errorMessage}</p>
                  </div>
                </div>
              )}

              {!createdUser ? (
                <>
                  {/* Instrucciones */}
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-3">
                      Registro de nuevo usuario
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Completa todos los campos obligatorios marcados con (*)</li>
                        <li>• El email debe ser único en el sistema</li>
                        <li>• La contraseña debe tener al menos 6 caracteres</li>
                        {/* <li>• Los roles se pueden modificar posteriormente</li> */}
                      </ul>
                    </div>
                  </div>

                  {/* Formulario */}
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                      {/* Columna izquierda: Info básica */}
                      <div className="lg:col-span-1 space-y-6">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <User className="w-5 h-5 text-purple-500" />
                          Información Básica
                        </h3>
                        
                        <div className="bg-gray-50 rounded-xl p-5 border border-gray-100 space-y-5">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Nombre Completo *
                            </label>
                            <input
                              type="text"
                              {...register('nombreCompleto')}
                              placeholder="Juan Carlos Pérez"
                              className={`w-full px-4 py-3 border rounded-lg transition-all text-sm ${
                                errors.nombreCompleto
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.nombreCompleto && (
                              <p className="mt-1.5 text-sm text-red-500">{errors.nombreCompleto.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Email *
                            </label>
                            <input
                              type="email"
                              {...register('email')}
                              placeholder="usuario@ejemplo.com"
                              className={`w-full px-4 py-3 border rounded-lg transition-all text-sm ${
                                errors.email
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.email && (
                              <p className="mt-1.5 text-sm text-red-500">{errors.email.message}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Contraseña *
                            </label>
                            <input
                              type="password"
                              {...register('password')}
                              placeholder="Contraseña inicial"
                              className={`w-full px-4 py-3 border rounded-lg transition-all text-sm ${
                                errors.password
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.password && (
                              <p className="mt-1.5 text-sm text-red-500">{errors.password.message}</p>
                            )}
                            
                            {password && (
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-gray-500">Fortaleza:</span>
                                  <span className={`text-xs font-medium ${passwordStrength.color}`}>
                                    {passwordStrength.label}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div 
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                      passwordStrength.score <= 2 ? 'bg-red-500' :
                                      passwordStrength.score === 3 ? 'bg-yellow-500' :
                                      passwordStrength.score === 4 ? 'bg-green-500' :
                                      'bg-green-600'
                                    }`}
                                    style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                                  ></div>
                                </div>
                                {passwordStrength.suggestions.length > 0 && (
                                  <div className="text-xs text-gray-500">
                                    {passwordStrength.suggestions.map((suggestion, index) => (
                                      <div key={index} className="flex items-center">
                                        <XCircle className="w-3 h-3 text-red-400 mr-1 shrink-0" />
                                        {suggestion}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                              Confirmar Contraseña *
                            </label>
                            <input
                              type="password"
                              {...register('confirmPassword')}
                              placeholder="Repetir contraseña"
                              className={`w-full px-4 py-3 border rounded-lg transition-all text-sm ${
                                errors.confirmPassword
                                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                                  : 'border-gray-300 focus:border-purple-500 focus:ring-purple-500/20'
                              } focus:ring-4 focus:outline-none`}
                            />
                            {errors.confirmPassword && (
                              <p className="mt-1.5 text-sm text-red-500">{errors.confirmPassword.message}</p>
                            )}
                            
                            {confirmPassword && (
                              <div className="mt-2">
                                {password === confirmPassword ? (
                                  <div className="flex items-center text-green-600">
                                    <CheckCircle className="w-4 h-4 mr-2 shrink-0" />
                                    <span className="text-xs font-medium">Las contraseñas coinciden</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center text-red-600">
                                    <XCircle className="w-4 h-4 mr-2 shrink-0" />
                                    <span className="text-xs font-medium">Las contraseñas no coinciden</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-xs text-yellow-800">
                              💡 <strong>Nota:</strong> Se recomendará al usuario cambiar la contraseña en el primer inicio de sesión
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Columna derecha: Tipo de usuario y Rol */}
                      <div className="lg:col-span-2 space-y-8">
                        {/* Tipo de usuario - Card selector */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            <Shield className="w-5 h-5 text-purple-500" />
                            Tipo de Usuario
                          </h3>
                          <Controller
                            name="tipoUsuario"
                            control={control}
                            render={({ field }) => (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                {tiposUsuario.map((tipo) => {
                                  const Icon = tipo.icon;
                                  const isSelected = field.value === tipo.value;
                                  return (
                                    <button
                                      key={tipo.value}
                                      type="button"
                                      onClick={() => field.onChange(tipo.value)}
                                      className={`relative flex flex-col items-center text-center p-5 rounded-xl border-2 transition-all duration-200 ${
                                        isSelected
                                          ? 'border-purple-500 bg-purple-50 shadow-md'
                                          : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm'
                                      }`}
                                    >
                                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                                        isSelected ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'
                                      }`}>
                                        <Icon className="w-6 h-6" />
                                      </div>
                                      <p className={`font-semibold text-sm ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
                                        {tipo.label}
                                      </p>
                                      <p className={`text-xs mt-1 ${isSelected ? 'text-purple-700' : 'text-gray-500'}`}>
                                        {tipo.description}
                                      </p>
                                      {isSelected && (
                                        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center">
                                          <Check className="w-3 h-3" />
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          />
                          {errors.tipoUsuario && (
                            <p className="mt-2 text-sm text-red-500">{errors.tipoUsuario.message}</p>
                          )}
                        </div>

                        {/* Selección de Rol - Card selector */}
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
                            <LayoutGrid className="w-5 h-5 text-purple-500" />
                            Rol del Usuario
                          </h3>
                          
                          {isLoadingRoles ? (
                            <div className="flex items-center justify-center py-8 border border-gray-200 rounded-xl bg-gray-50">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                              <span className="ml-2 text-sm text-gray-500">Cargando roles...</span>
                            </div>
                          ) : (
                            <Controller
                              name="rol"
                              control={control}
                              render={({ field }) => (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  {roles.map((rol) => {
                                    const isSelected = field.value === rol.id;
                                    const RolIcon = rol.nombre.toLowerCase().includes('admin') ? Shield : 
                                          rol.nombre.toLowerCase().includes('vende') ? UserCog :
                                          rol.nombre.toLowerCase().includes('caja') ? Settings :
                                          Users;
                                    return (
                                      <button
                                        key={rol.id}
                                        type="button"
                                        onClick={() => field.onChange(rol.id)}
                                        className={`relative flex items-start gap-4 p-5 rounded-xl border-2 text-left transition-all duration-200 ${
                                          isSelected
                                            ? 'border-purple-500 bg-purple-50 shadow-md'
                                            : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm'
                                        }`}
                                      >
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                          isSelected ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'
                                        }`}>
                                          <RolIcon className="w-6 h-6" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`font-semibold text-sm ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
                                            {rol.nombre}
                                          </p>
                                          {rol.descripcion && (
                                            <p className={`text-xs mt-1 line-clamp-2 ${isSelected ? 'text-purple-700' : 'text-gray-500'}`}>
                                              {rol.descripcion}
                                            </p>
                                          )}
                                        </div>
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                                          isSelected ? 'bg-purple-500 border-purple-500' : 'border-gray-300'
                                        }`}>
                                          {isSelected && <Check className="w-3.5 h-3.5 text-white" />}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            />
                          )}
                          {errors.rol && (
                            <p className="mt-2 text-sm text-red-500">{errors.rol.message}</p>
                          )}
                          <p className="mt-2 text-xs text-gray-500">
                            Un usuario solo puede tener un rol asignado
                          </p>
                        </div>
                      </div>
                    </div>
                  </form>
                </>
              ) : (
                /* Resultados de la creación */
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Usuario creado exitosamente
                    </h3>
                  </div>

                  {/* Información del usuario creado */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-3">Información del usuario</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Nombre:</span>
                        <span className="text-sm font-medium text-gray-900">{createdUser.nombreCompleto}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Email:</span>
                        <span className="text-sm font-medium text-gray-900">{createdUser.email}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Tipo:</span>
                        <span className="text-sm font-medium text-gray-900">{createdUser.tipoUsuario}</span>
                      </div>
                      {createdUser.usuario && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-500">Usuario:</span>
                          <span className="text-sm font-medium text-gray-900">@{createdUser.usuario}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">ID:</span>
                        <span className="text-sm font-mono text-gray-900">{createdUser.id}</span>
                      </div>
                    </div>
                  </div>

                  {/* Información adicional */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <p className="text-sm text-blue-800 font-medium">Usuario listo para usar</p>
                        <p className="text-sm text-blue-700 mt-1">El usuario puede iniciar sesión con sus credenciales. Se recomienda que cambie su contraseña en el primer acceso.</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-8 py-5 bg-gray-50 border-t border-gray-200">
              {!createdUser ? (
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium text-sm"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    onClick={handleSubmit(onSubmit)}
                    disabled={isLoading}
                    className="px-8 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm shadow-md"
                  >
                    {isLoading ? 'Creando...' : 'Crear Usuario'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleContinue}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-4 rounded-lg hover:opacity-90 transition-all font-medium shadow-md"
                >
                  Continuar
                </button>
              )}
            </div>
          </div>
        </section>
      </div>
      
      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default CreateUserDrawer;